import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { mediaUrl } from "./media";

const SettingsCtx = createContext({ settings: null });

/** Idempotently upsert a <meta> tag (by name OR property) with the given content. */
function upsertMeta({ name, property, content }) {
  if (!content) return;
  const sel = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Inject (or replace) a managed <script> block by id. */
function upsertScript(id, attrs = {}, body = "") {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const s = document.createElement("script");
  s.id = id;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  if (body) s.text = body;
  document.head.appendChild(s);
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [menu, setMenu] = useState([]);

  useEffect(() => {
    // Try the fast bootstrap endpoint first (1 request returns settings + menu).
    // Falls back to legacy endpoints if /bootstrap isn't deployed yet.
    api.get("/bootstrap")
      .then((r) => {
        const b = r.data || {};
        if (b.settings) setSettings(b.settings);
        if (b.menu) setMenu(b.menu);
      })
      .catch(() => {
        // Fallback for old backends that don't have /bootstrap yet
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
        api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
      });
  }, []);

  // Sync browser <title> + SEO meta tags + analytics scripts from settings
  useEffect(() => {
    if (!settings) return;
    const name = settings.site_name || "Tara Autohub";
    const tag = settings.seo_tagline || "";
    document.title = settings.seo_title?.trim() || (tag ? `${name} — ${tag}` : name);

    const desc = settings.seo_description || "";
    const keywords = settings.seo_keywords || "";
    const ogTitle = settings.seo_og_title || document.title;
    const ogDesc = settings.seo_og_description || desc;
    const ogImage = settings.seo_og_image ? mediaUrl(settings.seo_og_image) : "";
    const canonical = settings.seo_canonical_url || "";

    if (desc) upsertMeta({ name: "description", content: desc });
    if (keywords) upsertMeta({ name: "keywords", content: keywords });
    upsertMeta({ property: "og:title", content: ogTitle });
    if (ogDesc) upsertMeta({ property: "og:description", content: ogDesc });
    if (ogImage) {
      const absImage = ogImage.startsWith("http") ? ogImage : `${window.location.origin}${ogImage}`;
      upsertMeta({ property: "og:image", content: absImage });
      upsertMeta({ name: "twitter:image", content: absImage });
    }
    upsertMeta({ name: "twitter:title", content: ogTitle });
    if (ogDesc) upsertMeta({ name: "twitter:description", content: ogDesc });

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // --- Google Analytics 4 ---
    const gaId = (settings.analytics_ga_id || "").trim();
    if (gaId) {
      upsertScript("ga4-loader", { async: "true", src: `https://www.googletagmanager.com/gtag/js?id=${gaId}` });
      upsertScript("ga4-init", {}, `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${gaId}');`);
    }

    // --- Google Tag Manager ---
    const gtmId = (settings.analytics_gtm_id || "").trim();
    if (gtmId) {
      upsertScript("gtm-init", {}, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`);
    }

    // --- Meta (Facebook) Pixel ---
    const pixelId = (settings.analytics_meta_pixel_id || "").trim();
    if (pixelId) {
      upsertScript("meta-pixel", {}, `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');
fbq('track', 'PageView');`);
    }

    // --- Advanced: raw custom head HTML/scripts (kept inside a managed container) ---
    const custom = (settings.analytics_custom_head || "").trim();
    let host = document.getElementById("tah-custom-head");
    if (custom) {
      if (!host) {
        host = document.createElement("div");
        host.id = "tah-custom-head";
        host.style.display = "none";
        document.head.appendChild(host);
      }
      host.innerHTML = custom;
      // execute any <script> tags inside custom HTML (innerHTML doesn't run them)
      host.querySelectorAll("script").forEach((old) => {
        const s = document.createElement("script");
        Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value));
        s.text = old.textContent || "";
        old.replaceWith(s);
      });
    } else if (host) {
      host.innerHTML = "";
    }
  }, [settings]);

  return <SettingsCtx.Provider value={{ settings, menu, refresh: async () => {
    const s = await api.get("/settings"); setSettings(s.data);
    const m = await api.get("/menu"); setMenu(m.data);
  } }}>{children}</SettingsCtx.Provider>;
}

export const useSettings = () => useContext(SettingsCtx);
